import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Alert,
  Divider,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Upload,
  Instagram,
  CheckCircle,
  Add,
  TrendingUp,
  CheckCircleOutline,
  AttachMoney,
  Close,
  LinkOff,
  AutoFixHigh as AutoFixHighIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Import existing services
import { getShowcases, createShowcase, deleteShowcase } from '../../services/showcaseService';
import {
  checkInstagramTokenStatus,
  initiateInstagramAuth,
  getInstagramPosts,
  syncInstagramPosts,
  disconnectInstagram
} from '../../services/instagramService';
import { isMobileDevice, openOAuthPopup, monitorPopup } from '../../utils/mobileOAuthHandler';
import { addShowcaseCredits } from '../../services/creditService';
import ShowcaseCard from '../showcase/ShowcaseCard';
import type { ShowcaseData } from '../showcase/ShowcaseCard';
import ShowcaseForm from '../showcase/ShowcaseForm';
import type { Showcase } from '@app-types/models/showcase';
import type { InstagramPost } from '@app-types/models/instagram';

interface QuickShowcaseFormData {
  stylistId?: string;
  businessId?: string;
  showcases?: Showcase[];
  [key: string]: unknown;
}

interface QuickShowcaseStepProps {
  formData: QuickShowcaseFormData;
  onUpdate: (data: Record<string, unknown>) => void;
  onNext: () => void;
  onBack: () => void;
  onError: (error: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  triggerInstagramAnalysis?: (stylistId: string) => void;
  isAnalyzing?: boolean;
  aiAnalysisError?: string;
}

// Extend Window for global ref
declare global {
  interface Window {
    isAnalysisTriggeredRef?: React.MutableRefObject<boolean>;
  }
}

const QuickShowcaseStep: React.FC<QuickShowcaseStepProps> = ({
  formData,
  onUpdate,
  onNext,
  onBack,
  onError,
  isLoading,
  setIsLoading,
  triggerInstagramAnalysis,
  isAnalyzing,
  aiAnalysisError
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // State for showcases
  const [showcases, setShowcases] = useState<Showcase[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showcaseDescription, setShowcaseDescription] = useState<string>('');
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false);

  // State for Instagram
  const [isInstagramConnected, setIsInstagramConnected] = useState<boolean>(false);
  const [instagramPosts, setInstagramPosts] = useState<InstagramPost[]>([]);
  const [selectedInstagramPosts, setSelectedInstagramPosts] = useState<string[]>([]);
  const [isInstagramDialogOpen, setIsInstagramDialogOpen] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  // State for edit and delete operations
  const [selectedShowcase, setSelectedShowcase] = useState<Showcase | null>(null);
  const [isShowcaseFormOpen, setIsShowcaseFormOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [showcaseToDelete, setShowcaseToDelete] = useState<Showcase | null>(null);

  // Refs to prevent multiple concurrent API calls and store callbacks
  const isLoadingShowcasesRef = useRef<boolean>(false);
  const isCheckingInstagramRef = useRef<boolean>(false);
  const isAnalysisTriggeredRef = useRef<boolean>(false);
  const onUpdateRef = useRef(onUpdate);
  const triggerInstagramAnalysisRef = useRef(triggerInstagramAnalysis);

  // Update the ref when onUpdate or triggerInstagramAnalysis changes
  useEffect(() => {
    onUpdateRef.current = onUpdate;
    triggerInstagramAnalysisRef.current = triggerInstagramAnalysis;
    // Expose the analysis trigger ref globally for reset
    window.isAnalysisTriggeredRef = isAnalysisTriggeredRef;
  }, [onUpdate, triggerInstagramAnalysis]);

  // Helper function to award showcase credits
  const awardShowcaseCredits = async (newShowcaseCount: number): Promise<void> => {
    console.log('awardShowcaseCredits called with count:', newShowcaseCount);
    console.log('FormData available:', formData);

    try {
      // In quick onboarding, get business ID from formData instead of localStorage
      // because user hasn't completed the full login process yet
      let businessId = formData.businessId;
      console.log('Business ID from formData:', businessId);

      // Fallback to localStorage for other cases (like existing users)
      if (!businessId) {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        console.log('Fallback - User from localStorage:', user);
        const fallbackBusinessId = user?.businessId || user?.business_id;
        console.log('Fallback Business ID:', fallbackBusinessId);

        if (!fallbackBusinessId) {
          console.warn('No business ID found in formData or localStorage');
          return;
        }

        // Use fallback business ID
        businessId = fallbackBusinessId;
      }

      // Calculate credits (100 HKD per showcase, max 1000 HKD)
      const creditPerShowcase = 100;
      const maxCredit = 1000;
      const creditAmount = Math.min(newShowcaseCount * creditPerShowcase, maxCredit);

      console.log('Credit calculation:', {
        showcaseCount: newShowcaseCount,
        creditPerShowcase,
        maxCredit,
        creditAmount
      });

      if (creditAmount > 0) {
        console.log('About to call addShowcaseCredits API with:', {
          businessId,
          showcaseCount: newShowcaseCount,
          creditAmount
        });

        const result = await addShowcaseCredits({
          businessId: businessId as string,
          showcaseCount: newShowcaseCount,
          creditAmount
        });

        console.log('API call successful:', result);

        toast.success(t('quickOnboarding.showcase.creditsAwarded',
          'Credits awarded: {{amount}} HKD for {{count}} showcase(s)!',
          { amount: creditAmount, count: newShowcaseCount }
        ));
      } else {
        console.log('Credit amount is 0, skipping API call');
      }
    } catch (error: unknown) {
      // Don't block the flow if credit awarding fails, just log it
      const err = error as { message?: string; response?: { data?: unknown; status?: number } };
      console.error('Error awarding showcase credits:', error);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });

      if (err.message?.includes('already been added')) {
        console.log('Credits already awarded for this business');
      } else {
        toast.warning(t('quickOnboarding.showcase.creditError',
          'Showcase uploaded successfully, but there was an issue with credit awarding'
        ));
      }
    }
  };


  // Check Instagram connection status
  const checkInstagramConnection = useCallback(async (): Promise<void> => {
    if (!formData.stylistId || isCheckingInstagramRef.current) return;

    isCheckingInstagramRef.current = true;
    try {
      const { hasToken } = await checkInstagramTokenStatus(formData.stylistId!);
      setIsInstagramConnected(!!hasToken);
    } catch (error: unknown) {
      console.error('Error checking Instagram connection:', error);
    } finally {
      isCheckingInstagramRef.current = false;
    }
  }, [formData.stylistId]);

  // Fetch showcases - memoized to prevent infinite loops
  const fetchShowcases = useCallback(async (): Promise<Showcase[]> => {
    if (!formData.stylistId || isLoadingShowcasesRef.current) return [];

    isLoadingShowcasesRef.current = true;
    try {
      const response = await getShowcases(formData.stylistId!);
      const data = Array.isArray(response.data) ? response.data : [];
      setShowcases(data);
      return data;
    } catch (error: unknown) {
      console.error('Error fetching showcases:', error);
      return [];
    } finally {
      isLoadingShowcasesRef.current = false;
    }
  }, [formData.stylistId]); // No onUpdate dependency to prevent loops

  // Separate effect to notify parent when showcases change
  useEffect(() => {
    if (showcases.length >= 0) { // Only notify when we have valid showcase data
      onUpdateRef.current({ showcases });
    }
  }, [showcases]); // Only depend on showcases, not onUpdate

  // Initialize component - only run when stylistId changes
  useEffect(() => {
    if (formData.stylistId) {
      checkInstagramConnection();
      fetchShowcases();
    }
  }, [formData.stylistId, checkInstagramConnection, fetchShowcases]);



  // Handle Instagram authentication return
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const instagramSuccess = searchParams.get('instagram_success');

    if (instagramSuccess === 'true') {
      // Don't directly set connection status - re-check via API to get accurate status
      console.log('Instagram auth callback detected - re-checking connection status via API');

      // Check connection status first
      const handleInstagramSuccess = async (): Promise<void> => {
        await checkInstagramConnection();

        // Trigger AI analysis in the background once Instagram is connected
        if (triggerInstagramAnalysisRef.current && formData.stylistId && !isAnalysisTriggeredRef.current) {
          isAnalysisTriggeredRef.current = true;
          // Add a small delay to ensure Instagram connection is fully processed
          setTimeout(() => {
            if (triggerInstagramAnalysisRef.current && formData.stylistId) {
              triggerInstagramAnalysisRef.current(formData.stylistId);
            }
          }, 1000);
        }
      };

      handleInstagramSuccess();

      // Clean up URL parameter to prevent future confusion
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('instagram_success');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [location.search, checkInstagramConnection, formData.stylistId]);

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newFiles = Array.from(event.target.files || []);
    // Accumulate files with deduplication based on file name and size
    setSelectedFiles(prevFiles => {
      const existingFileKeys = prevFiles.map(f => `${f.name}-${f.size}`);
      const uniqueNewFiles = newFiles.filter(file =>
        !existingFileKeys.includes(`${file.name}-${file.size}`)
      );
      return [...prevFiles, ...uniqueNewFiles];
    });
    // Reset input to allow selecting the same files again
    event.target.value = '';
  };

  // Handle removing a selected file
  const handleRemoveSelectedFile = (index: number): void => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  // Handle file upload
  const handleUploadFiles = async (): Promise<void> => {
    if (selectedFiles.length === 0) {
      onError('Please select files');
      return;
    }

    console.log('QuickShowcaseStep - About to upload files:', selectedFiles.length);
    console.log('Selected files details:', selectedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })));

    setIsLoading(true);

    try {
      const result = await createShowcase({
        files: selectedFiles,
        content: showcaseDescription || 'Quick onboarding showcase',
        planIds: JSON.stringify([]),
        stylistId: formData.stylistId!
      });

      console.log('QuickShowcaseStep - Upload result:', result);

      // Refresh showcases and get updated count
      const updatedShowcases = await fetchShowcases();
      console.log('After file upload - updated showcases:', updatedShowcases);

      // Reset form
      setSelectedFiles([]);
      setShowcaseDescription('');
      setIsUploadDialogOpen(false);

      toast.success('Showcase uploaded successfully!');
    } catch (error: unknown) {
      const err = error as Error;
      console.error('QuickShowcaseStep - Error uploading showcase:', error);
      onError(err.message || 'Failed to upload showcase');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Instagram authentication
  const handleInstagramAuth = async (): Promise<void> => {
    console.log('Initiating Instagram auth with stylistId (staff.id):', formData.stylistId);
    setIsLoading(true);

    try {
      // For mobile popup flow, redirect to callback handler page
      const returnUrl = isMobileDevice()
        ? `${window.location.origin}/oauth-callback`
        : `${window.location.origin}/quick-onboarding?step=3&instagram_success=true`;

      const { authUrl } = await initiateInstagramAuth(formData.stylistId!, returnUrl);

      if (!authUrl) {
        throw new Error('No auth URL received from backend');
      }

      // Mobile browser: Use popup to prevent Instagram app intercept
      if (isMobileDevice()) {
        console.log('Mobile browser detected - using popup approach');

        // CRITICAL: Open popup BEFORE any async operations (iOS requirement)
        const popup = openOAuthPopup(authUrl);

        // Check if popup was blocked
        if (!popup || popup.closed) {
          console.warn('Popup was blocked by browser');
          setIsLoading(false);

          // Fallback: Navigate to intermediate page
          navigate('/oauth-redirect', {
            state: {
              authUrl: authUrl,
              returnPath: location.pathname + location.search
            }
          });
          return;
        }

        console.log('OAuth popup opened successfully');

        // Monitor popup for completion
        monitorPopup(
          popup,
          (_token: string) => {
            // Success: Token will be in URL, reload to process it
            console.log('OAuth completed successfully');
            setIsLoading(false);
            // Reload to process the callback
            window.location.reload();
          },
          (error: Error) => {
            // Error: Show message
            console.error('OAuth popup error:', error);
            setIsLoading(false);
            onError(error.message || 'Failed to connect Instagram');
          }
        );

      } else {
        // Desktop browser: Use traditional redirect (works perfectly)
        console.log('Desktop browser detected - using traditional redirect');
        window.location.href = authUrl;
      }

    } catch (error: unknown) {
      const err = error as Error;
      console.error('Error with Instagram authentication:', error);
      onError(err.message || 'Failed to connect Instagram');
      setIsLoading(false);
    }
  };

  // Handle loading Instagram posts
  const handleLoadInstagramPosts = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await getInstagramPosts(formData.stylistId!);
      const posts = response.data;
      setInstagramPosts(posts);
      setIsInstagramDialogOpen(true);
    } catch (error: unknown) {
      const err = error as Error;
      console.error('Error loading Instagram posts:', error);
      onError(err.message || 'Failed to load Instagram posts');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Instagram post selection
  const handleInstagramPostToggle = (postId: string): void => {
    setSelectedInstagramPosts(prev =>
      prev.includes(postId)
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  // Handle syncing Instagram posts
  const handleSyncInstagramPosts = async (): Promise<void> => {
    if (selectedInstagramPosts.length === 0) {
      onError('Please select at least one post');
      return;
    }

    setIsSyncing(true);

    try {
      await syncInstagramPosts(formData.stylistId!, selectedInstagramPosts);

      // Refresh showcases and get updated count
      const updatedShowcases = await fetchShowcases();
      console.log('After Instagram sync - updated showcases:', updatedShowcases);

      setIsInstagramDialogOpen(false);
      setSelectedInstagramPosts([]);

      toast.success('Instagram posts synced successfully!');
    } catch (error: unknown) {
      const err = error as Error;
      console.error('Error syncing Instagram posts:', error);
      onError(err.message || 'Failed to sync Instagram posts');
    } finally {
      setIsSyncing(false);
    }
  };

  // Handle disconnecting Instagram
  const handleDisconnectInstagram = async (): Promise<void> => {
    try {
      await disconnectInstagram(formData.stylistId!);
      setIsInstagramConnected(false);
      setIsInstagramDialogOpen(false);
      setInstagramPosts([]);
      setSelectedInstagramPosts([]);
      toast.success('Instagram disconnected successfully!');
    } catch (error: unknown) {
      const err = error as Error;
      onError(err.message || 'Failed to disconnect Instagram');
    }
  };

  // Handle next step
  const handleNext = async (): Promise<void> => {
    console.log('handleNext called - current showcases:', showcases);

    if (showcases.length < minimumShowcases) {
      console.log(`Not enough showcases found (${showcases.length}/${minimumShowcases}), preventing continue`);
      onError(`Please upload at least ${minimumShowcases} showcases before continuing`);
      return;
    }

    // Award credits for all showcases when user continues to next step
    // Note: Backend has duplicate prevention, so this is safe to call
    try {
      console.log('Calling awardShowcaseCredits from handleNext');
      await awardShowcaseCredits(showcases.length);
    } catch (error: unknown) {
      // Don't block progression if credit awarding fails
      const err = error as Error;
      console.log('Credit awarding error on next step:', err.message);
    }

    console.log('Proceeding to next step');
    onNext();
  };

  // Handle edit showcase
  const handleEditShowcase = (showcase: Showcase): void => {
    console.log('Opening full showcase edit form for:', showcase._id);
    setSelectedShowcase(showcase);
    setIsShowcaseFormOpen(true);
  };

  // Handle close showcase form
  const handleCloseShowcaseForm = async (): Promise<void> => {
    setIsShowcaseFormOpen(false);
    setSelectedShowcase(null);

    // Refresh showcases
    await fetchShowcases();
  };

  // Handle delete showcase
  const handleDeleteShowcase = (showcase: Showcase): void => {
    setShowcaseToDelete(showcase);
    setIsDeleteDialogOpen(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async (): Promise<void> => {
    if (!showcaseToDelete) return;

    setIsLoading(true);
    try {
      await deleteShowcase(showcaseToDelete._id);

      // Refresh showcases
      await fetchShowcases();
      toast.success(t('quickOnboarding.showcase.deleteSuccess'));

      // Close dialog
      setIsDeleteDialogOpen(false);
      setShowcaseToDelete(null);
    } catch (error: unknown) {
      console.error('Error deleting showcase:', error);
      toast.error(t('quickOnboarding.showcase.deleteError'));
    } finally {
      setIsLoading(false);
    }
  };

  const minimumShowcases = 1;
  // Count both existing showcases and selected Instagram posts that will be synced
  const totalShowcaseCount = showcases.length + selectedInstagramPosts.length;
  const hasEnoughShowcases = totalShowcaseCount >= minimumShowcases;

  return (
    <Box className="showcase-step">
      <Typography variant="h5" gutterBottom>
        {t('quickOnboarding.showcase.title', 'Show Your Best Work')}
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        {t('quickOnboarding.showcase.description', 'Upload your best work to attract more clients and stand out from the competition')}
      </Typography>

      {/* AI Analysis Information */}
      <Card elevation={1} sx={{ mb: 3, backgroundColor: 'rgba(25, 118, 210, 0.04)', border: '1px solid rgba(25, 118, 210, 0.12)' }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AutoFixHighIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                {t('quickOnboarding.showcase.aiFeatureTitle', 'AI-Powered Profile Setup')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('quickOnboarding.showcase.aiFeatureDesc', 'Connect Instagram and our AI will analyze your posts to automatically fill out your profile information, including services, experience, and languages.')}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Credit Rules */}
      <Alert severity="info" className="credit-alert" sx={{ mb: 3 }} icon={<AttachMoney />}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {t('quickOnboarding.showcase.creditRules', 'Earn 100 HKD Credits per showcase (Maximum 1,000 HKD)')}
        </Typography>
      </Alert>

      {/* AI Analysis Status */}
      {isAnalyzing && (
        <Alert severity="info" className="ai-analysis-alert" sx={{ mb: 3 }} icon={<CircularProgress size={20} />}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {t('quickOnboarding.showcase.aiAnalyzing', 'Analyzing your Instagram profile to help pre-fill your business details...')}
          </Typography>
        </Alert>
      )}

      {aiAnalysisError && (
        <Alert severity="warning" className="ai-analysis-error-alert" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('quickOnboarding.showcase.aiAnalysisError', 'Profile analysis failed, but you can still fill out your profile manually.')}
          </Typography>
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Progress indicator with credit information */}
      <Alert
        severity={hasEnoughShowcases ? 'success' : 'warning'}
        className={`progress-alert ${hasEnoughShowcases ? 'success' : ''}`}
        sx={{
          mb: 3,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        icon={hasEnoughShowcases ? <CheckCircle /> : <TrendingUp />}
      >
        <Typography
          variant={hasEnoughShowcases ? "body1" : "h6"}
          sx={{
            mb: 1,
            fontWeight: hasEnoughShowcases ? 'normal' : 'bold',
            fontSize: hasEnoughShowcases ? 'inherit' : '1.1rem'
          }}
        >
          {hasEnoughShowcases
            ? t('quickOnboarding.showcase.ready', 'Great! You have {{count}} showcase(s). You\'re ready to continue.', { count: totalShowcaseCount })
            : t('quickOnboarding.showcase.need', 'You need at least {{minimum}} showcases to continue. You currently have {{current}}.', {
                minimum: minimumShowcases,
                current: totalShowcaseCount
              })
          }
        </Typography>
        {!hasEnoughShowcases && (
          <Typography variant="body2" sx={{ mb: 1, color: 'warning.dark', fontWeight: 'medium' }}>
            {t('quickOnboarding.showcase.remaining', 'Please upload {{remaining}} more showcase(s) to proceed.', {
              remaining: minimumShowcases - totalShowcaseCount
            })}
          </Typography>
        )}
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
          {t('quickOnboarding.showcase.creditInfo', 'Credits: {{credits}} HKD earned (100 HKD per showcase, max 1,000 HKD)', {
            credits: Math.min(totalShowcaseCount * 100, 1000)
          })}
        </Typography>
      </Alert>

      {/* Upload options */}
      <Box className="upload-options" sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          {t('quickOnboarding.showcase.addContent', 'Add Your Content')}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card className="upload-card" sx={{ height: '100%', cursor: 'pointer' }} onClick={() => setIsUploadDialogOpen(true)}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Upload color="primary" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('quickOnboarding.showcase.uploadDirect', 'Upload Photos/Videos')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('quickOnboarding.showcase.uploadDesc', 'Upload your best work directly from your device')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            {!isInstagramConnected ? (
              <Card className="upload-card" sx={{ height: '100%', cursor: 'pointer' }} onClick={handleInstagramAuth}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Instagram color="primary" sx={{ fontSize: 48, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('quickOnboarding.showcase.connectInstagram', 'Connect Instagram')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('quickOnboarding.showcase.instagramDesc', 'Import your existing Instagram posts')}
                  </Typography>
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                    {t('quickOnboarding.showcase.aiHelpDesc', 'AI will help pre-populate your profile information')}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Card className="upload-card" sx={{ height: '100%', cursor: 'pointer' }} onClick={handleLoadInstagramPosts}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <CheckCircleOutline color="success" sx={{ fontSize: 48, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('quickOnboarding.showcase.instagramConnected', 'Instagram Connected')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('quickOnboarding.showcase.selectPosts', 'Select posts to import')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Existing showcases */}
      {showcases.length > 0 && (
        <Box className="showcases-grid" sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            {t('quickOnboarding.showcase.yourShowcases', 'Your Showcases')}
          </Typography>

          <Grid container spacing={2}>
            {showcases.map((showcase) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={showcase._id}>
                <ShowcaseCard
                  showcase={showcase as ShowcaseData}
                  onEdit={(s) => handleEditShowcase(s as Showcase)}
                  onDelete={(s) => handleDeleteShowcase(s as Showcase)}
                  showActions={true}
                  showStatus={true}
                  showContent={true}
                  contentMaxLength={60}
                  elevation={1}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Navigation buttons */}
      <Box className="navigation-buttons" sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={isLoading}
          className="back-button"
        >
          {t('quickOnboarding.buttons.back')}
        </Button>

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!hasEnoughShowcases || isLoading || isAnalyzing}
          className="continue-button"
        >
          {isAnalyzing
            ? t('quickOnboarding.showcase.analyzing', 'AI is analyzing...')
            : t('quickOnboarding.showcase.continue', 'Continue to Profile')
          }
        </Button>
      </Box>

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onClose={() => setIsUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('quickOnboarding.showcase.uploadTitle', 'Upload Showcase')}</DialogTitle>
        <DialogContent>
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            id="file-upload"
          />
          <label htmlFor="file-upload">
            <Button
              variant="outlined"
              component="span"
              fullWidth
              startIcon={<Add />}
              sx={{ mb: 2 }}
            >
              {t('quickOnboarding.showcase.selectFiles', 'Select Files')}
            </Button>
          </label>

          {selectedFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                {t('quickOnboarding.showcase.selectedFiles', 'Selected files:')} ({selectedFiles.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedFiles.map((file, index) => (
                  <Chip
                    key={index}
                    label={file.name}
                    size="small"
                    onDelete={() => handleRemoveSelectedFile(index)}
                    deleteIcon={<Close fontSize="small" />}
                    sx={{
                      maxWidth: '250px',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('quickOnboarding.showcase.description', 'Description (optional)')}
            value={showcaseDescription}
            onChange={(e) => setShowcaseDescription(e.target.value)}
            placeholder={t('quickOnboarding.showcase.descriptionPlaceholder', 'Describe your work...')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsUploadDialogOpen(false)}>
            {t('quickOnboarding.showcase.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleUploadFiles}
            variant="contained"
            disabled={selectedFiles.length === 0 || isLoading}
          >
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              t('quickOnboarding.showcase.upload', 'Upload')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instagram Posts Dialog */}
      <Dialog open={isInstagramDialogOpen} onClose={() => setIsInstagramDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('quickOnboarding.showcase.instagramPosts', 'Select Instagram Posts')}</DialogTitle>
        <DialogContent>
          {instagramPosts.length > 0 ? (
            <Grid container spacing={2}>
              {instagramPosts.map((post) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={post.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedInstagramPosts.includes(post.id) ? 2 : 1,
                      borderColor: selectedInstagramPosts.includes(post.id) ? 'primary.main' : 'divider'
                    }}
                    onClick={() => handleInstagramPostToggle(post.id)}
                  >
                    {post.mediaType === 'VIDEO' ? (
                      <CardMedia
                        component="video"
                        height="200"
                        src={post.mediaUrl}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <CardMedia
                        component="img"
                        height="200"
                        image={post.mediaUrl}
                        sx={{ objectFit: 'cover' }}
                      />
                    )}
                    <CardContent sx={{ pb: 1 }}>
                      <Typography variant="body2" noWrap>
                        {post.caption?.substring(0, 60) || 'No caption'}
                        {post.caption && post.caption.length > 60 ? '...' : ''}
                      </Typography>
                      {selectedInstagramPosts.includes(post.id) && (
                        <Chip
                          size="small"
                          label={t('quickOnboarding.showcase.selected', 'Selected')}
                          color="primary"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {t('quickOnboarding.showcase.noInstagramPosts', 'No Instagram posts available')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            startIcon={<LinkOff />}
            onClick={handleDisconnectInstagram}
            color="error"
            variant="outlined"
          >
            {t('showcaseManagement.disconnectInstagram')}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setIsInstagramDialogOpen(false)}>
              {t('quickOnboarding.showcase.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleSyncInstagramPosts}
              variant="contained"
              disabled={selectedInstagramPosts.length === 0 || isSyncing}
            >
              {isSyncing ? (
                <CircularProgress size={20} />
              ) : (
                t('quickOnboarding.showcase.syncPosts', 'Sync Selected Posts')
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ShowcaseForm Dialog for Editing */}
      {isShowcaseFormOpen && (
        <ShowcaseForm
          selectedStylist={formData.stylistId!}
          showcase={selectedShowcase as React.ComponentProps<typeof ShowcaseForm>['showcase']}
          closeModal={handleCloseShowcaseForm}
          isInDialog={true}
          hidePlans={true}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('quickOnboarding.showcase.deleteTitle', 'Delete Showcase')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('quickOnboarding.showcase.deleteConfirmation', 'Are you sure you want to delete this showcase? This action cannot be undone.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>
            {t('quickOnboarding.showcase.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : t('quickOnboarding.showcase.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuickShowcaseStep;
